import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { whitelabelApi } from '../lib/api';

const WhitelabelContext = createContext(null);

const DEFAULTS = {
  app_name: 'BIL',
  app_subtitle: 'Business Intelligence Lounge',
  primary_color: '#002677',
  accent_color: '#41B6E6',
  logo_url: null,
  login_bg_url: null,
  favicon_url: null,
  custom_domain: null,
  footer_text: null,
};

export function WhitelabelProvider({ children }) {
  const [config, setConfig] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await whitelabelApi.getConfig();
      setConfig({ ...DEFAULTS, ...response.data });
    } catch (error) {
      // Use defaults on error
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Apply primary color as CSS variable
  useEffect(() => {
    if (config.primary_color && config.primary_color !== '#0047FF') {
      const hex = config.primary_color;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      // Convert to HSL for CSS variable
      const [h, s, l] = rgbToHsl(r, g, b);
      document.documentElement.style.setProperty('--primary', `${h} ${s}% ${l}%`);
      document.documentElement.style.setProperty('--ring', `${h} ${s}% ${l}%`);
    }
  }, [config.primary_color]);

  const updateConfig = async (updates) => {
    try {
      const response = await whitelabelApi.updateConfig(updates);
      setConfig({ ...DEFAULTS, ...response.data });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed' };
    }
  };

  const refreshConfig = () => fetchConfig();

  return (
    <WhitelabelContext.Provider value={{ config, updateConfig, refreshConfig, loaded }}>
      {children}
    </WhitelabelContext.Provider>
  );
}

export function useWhitelabel() {
  const context = useContext(WhitelabelContext);
  if (!context) {
    throw new Error('useWhitelabel must be used within a WhitelabelProvider');
  }
  return context;
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}
